import { useEffect, useState } from 'react';

/**
 * Returns the current clearance (px) needed above the fixed mobile bottom
 * navigation (#mobile-bottom-nav): its height while visible, or 0 when it
 * isn't in the DOM, is display:none (md+ screens), or has auto-hidden on
 * scroll (data-nav-hidden="true", set by Navigation while the bar is slid
 * out of view via transform — offsetHeight alone wouldn't notice that).
 * Bottom-anchored floating elements use this to always clear the nav —
 * including its env(safe-area-inset-bottom) padding — and to drop down to
 * the bottom edge in sync with the nav's hide animation.
 */
const useBottomNavOffset = () => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let resizeObserver = null;

    const measure = () => {
      const nav = document.getElementById('mobile-bottom-nav');
      // offsetHeight is 0 when the nav is display:none (md+), which
      // conveniently collapses the clearance to zero on desktop. While the
      // nav is auto-hidden on scroll it keeps its layout height (transform
      // slide), so the data flag collapses the clearance instead.
      const isHidden = nav?.dataset.navHidden === 'true';
      setOffset(nav && !isHidden ? nav.offsetHeight : 0);
      return nav;
    };

    const observeNav = (nav) => {
      if (nav && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => measure());
        resizeObserver.observe(nav);
      }
    };

    // Watch for the nav mounting/unmounting anywhere in the document, and for
    // its data-nav-hidden flag toggling as it auto-hides/reappears on scroll
    const mutationObserver = new MutationObserver((mutations) => {
      const nav = document.getElementById('mobile-bottom-nav');
      const isObservingNav = !!resizeObserver;
      if (!!nav !== isObservingNav) {
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
        measure();
        observeNav(nav);
      } else if (mutations.some((m) => m.type === 'attributes')) {
        measure();
      }
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-nav-hidden'],
    });

    observeNav(measure());
    window.addEventListener('resize', measure);

    return () => {
      mutationObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return offset;
};

export default useBottomNavOffset;
